// Counting a style's sizes on the shop floor (server: getFashionStockOnHand,
// submitFashionStockCount). The count is blind: staff scan or key in what they
// find, and only then see what the system expected. Saving it makes a draft
// stock adjustment (m_inventory) that the back office confirms, so nothing on
// the till changes stock by itself.
//
// A barcode adds a piece every time it is scanned. An RFID tag counts once,
// however often the reader picks it up, so a tag read twice is still one piece.
//
// Each count carries an id made when it starts. The server uses it as the
// draft's id, so a save sent again after a lost reply doesn't make a second
// draft.
export const MAX_COUNT_LINES = 200;
export const MAX_COUNTED = 99999;
const DRAFT_KEY = "fashionCountDraft";

const clean = (s) => String(s ?? "").replace(/["\\]/g, "").replace(/[\r\n]+/g, " ").trim();
const str = (s) => `"${clean(s)}"`;

export function buildStockOnHandQuery(bunitId, productIds) {
  const ids = [...new Set(productIds || [])].slice(0, MAX_COUNT_LINES);
  return { query: `query { getFashionStockOnHand(bunitId: ${str(bunitId)}, productIds: [${ids.map(str).join(", ")}]) { productId onHand }}` };
}

// Server response → { levels: Map<productId, onHand> } or { error }.
export function readStockOnHand(response) {
  const fallback = "Couldn't read the stock on hand. Check the connection and try again.";
  if (!response) return { error: fallback };
  const errors = response?.data?.errors;
  if (errors?.length) return { error: errors[0]?.message || fallback };
  const rows = response?.data?.data?.getFashionStockOnHand;
  if (!Array.isArray(rows)) return { error: fallback };
  return { levels: new Map(rows.map((r) => [r.productId, Number(r.onHand) || 0])) };
}

export function buildSubmitCountMutation(bunitId, note, lines, countId) {
  const items = lines.map((l) => `{ productId: ${str(l.productId)}, counted: ${Number(l.counted)} }`).join(", ");
  const n = clean(note);
  return {
    query: `mutation { submitFashionStockCount(count: { bunitId: ${str(bunitId)}${countId ? `, countId: ${str(countId)}` : ""}${n ? `, note: ${str(n.slice(0, 200))}` : ""}, lines: [${items}] }) { status message recordId documentNo } }`,
  };
}

// --- The count in progress -------------------------------------------------
// { countId, startedAt, order: [productId], qty: { [productId]: n },
//   tags: { [productId]: [tag] } }. A size's tags are its pieces counted by
// RFID; the rest of its qty was scanned or keyed in.
export function newCountId() {
  const hex = globalThis.crypto?.randomUUID?.().replace(/-/g, "") || Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join("");
  return hex.toUpperCase();
}

export const emptyCount = (now = Date.now()) => ({ countId: newCountId(), startedAt: now, order: [], qty: {}, tags: {} });

// Tags kept for a size never outnumber its pieces: taking pieces off drops
// the latest tags, so reading one of those tags again counts it again.
const fitTags = (tags, productId, qty) => {
  const list = tags[productId] || [];
  if (list.length <= qty) return tags;
  return { ...tags, [productId]: list.slice(0, qty) };
};

export const hasTag = (state, tag) => Object.values(state.tags || {}).some((list) => list.includes(tag));
export const taggedPieces = (state, productId) => (state.tags?.[productId] || []).length;

const clamp = (n) => Math.min(MAX_COUNTED, Math.max(0, Math.floor(n)));

function withLine(state, productId) {
  if (state.order.includes(productId)) return state;
  if (state.order.length >= MAX_COUNT_LINES) return state;
  return { ...state, order: [...state.order, productId], qty: { ...state.qty, [productId]: 0 } };
}

export function countReducer(state, action) {
  switch (action.type) {
    case "include": // put sizes on the sheet at 0 (found none until counted)
      return (action.productIds || []).reduce(withLine, state);
    case "add": {
      const s = withLine(state, action.productId);
      if (!s.order.includes(action.productId)) return state;
      const n = clamp((s.qty[action.productId] || 0) + (action.by ?? 1));
      return { ...s, qty: { ...s.qty, [action.productId]: n }, tags: fitTags(s.tags, action.productId, n) };
    }
    case "set": {
      const n = Number(action.qty);
      if (!state.order.includes(action.productId) || !Number.isFinite(n)) return state;
      return { ...state, qty: { ...state.qty, [action.productId]: clamp(n) }, tags: fitTags(state.tags, action.productId, clamp(n)) };
    }
    case "tag": {
      if (!action.tag || hasTag(state, action.tag)) return state;
      const s = countReducer(state, { type: "add", productId: action.productId });
      return s === state ? state : { ...s, tags: { ...s.tags, [action.productId]: [...(s.tags[action.productId] || []), action.tag] } };
    }
    case "remove": {
      if (!state.order.includes(action.productId)) return state;
      const qty = { ...state.qty };
      const tags = { ...state.tags };
      delete qty[action.productId];
      delete tags[action.productId];
      return { ...state, order: state.order.filter((id) => id !== action.productId), qty, tags };
    }
    case "restore":
      return action.state && Array.isArray(action.state.order) ? action.state : state;
    case "reset":
      return emptyCount();
    default:
      return state;
  }
}

export const isFull = (state) => state.order.length >= MAX_COUNT_LINES;
export const countedPieces = (state) => state.order.reduce((n, id) => n + (state.qty[id] || 0), 0);
export const countLines = (state) => state.order.map((productId) => ({ productId, counted: state.qty[productId] || 0 }));

// Counted against expected, for the review: rows with a difference first.
export function varianceRows(state, levels) {
  const rows = state.order.map((productId) => {
    const counted = state.qty[productId] || 0;
    const expected = levels?.get(productId);
    const known = expected != null;
    return { productId, counted, expected: known ? expected : null, diff: known ? counted - expected : null };
  });
  const differs = (r) => r.diff == null || Math.abs(r.diff) > 1e-9;
  return [...rows.filter(differs), ...rows.filter((r) => !differs(r))];
}

export const isRfidCode = (code) => /^RFID_/i.test(String(code || ""));

// A scanned code → { productId, tag } (tag set for an RFID read), or null.
// Barcodes and SKUs match the synced products; anything else is looked up in
// the till's RFID tags (rfidData: tag_value → product_code).
export async function resolveCountScan(code, products, productForCode, lookupTag) {
  const c = String(code || "").trim();
  if (!c) return null;
  if (!isRfidCode(c)) {
    const p = productForCode(products, c);
    if (p) return { productId: p.mProductId, tag: null };
  }
  let row = null;
  try {
    row = await lookupTag?.(c);
  } catch {
    row = null;
  }
  if (!row?.product_code) return null;
  const p = productForCode(products, row.product_code);
  return p ? { productId: p.mProductId, tag: c } : null;
}

// A draft older than this is flagged when it comes back: stock has moved since.
export const STALE_DRAFT_MS = 12 * 60 * 60 * 1000;
export const isStaleDraft = (state, now = Date.now()) => state.order.length > 0 && now - (Number(state.startedAt) || 0) > STALE_DRAFT_MS;

// The count is kept on this till until it is saved, so a reload doesn't lose it.
export function loadCountDraft(bunitId, storage = globalThis.localStorage) {
  if (!bunitId) return null;
  try {
    const raw = JSON.parse(storage.getItem(`${DRAFT_KEY}:${bunitId}`) || "null");
    if (!raw || !Array.isArray(raw.order) || typeof raw.qty !== "object" || !raw.qty) return null;
    const order = raw.order.slice(0, MAX_COUNT_LINES);
    const qty = {};
    const tags = {};
    order.forEach((id) => {
      const n = Number(raw.qty[id]);
      qty[id] = Number.isFinite(n) ? clamp(n) : 0;
      const list = raw.tags && !Array.isArray(raw.tags) && Array.isArray(raw.tags[id]) ? raw.tags[id] : [];
      if (list.length > 0 && qty[id] > 0) tags[id] = list.slice(0, qty[id]);
    });
    return {
      countId: /^[0-9A-F]{32}$/.test(raw.countId || "") ? raw.countId : newCountId(),
      startedAt: Number(raw.startedAt) || 0,
      order,
      qty,
      tags,
    };
  } catch {
    return null;
  }
}

export function saveCountDraft(bunitId, state, storage = globalThis.localStorage) {
  if (!bunitId) return;
  try {
    if (!state || state.order.length === 0) storage.removeItem(`${DRAFT_KEY}:${bunitId}`);
    else storage.setItem(`${DRAFT_KEY}:${bunitId}`, JSON.stringify(state));
  } catch {
    // Storage full or blocked: the count still works until the page reloads.
  }
}

// --- Finding what to count -------------------------------------------------
// The count's search box takes a style name, an item code typed with or
// without its dashes ("oxs110" finds OXS-110), or a SKU/barcode, in any case.
// An exact code is a single size; a name or part of a code may be many.
export const normCode = (s) =>
  String(s ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
const codesOf = (p) => [p.value, p.upc, p.sku].filter((c) => c != null && c !== "").map(normCode);
const words = (t) => String(t ?? "").toLowerCase().split(/\s+/).filter(Boolean);
const nameHas = (name, ws) => {
  const n = String(name ?? "").toLowerCase();
  return ws.length > 0 && ws.every((w) => n.includes(w));
};
// Part of a code needs a couple of characters, or every code would match.
const MIN_PART_CODE = 2;

// → { exact: [product], hits: [product] } — exact code matches first in hits.
export function matchCountProducts(products, term) {
  const ws = words(term);
  const n = normCode(term);
  const exact = [];
  const partial = [];
  if (ws.length === 0) return { exact, hits: [] };
  (products || []).forEach((p) => {
    if (!p?.mProductId || p.isactive === "N") return;
    const codes = codesOf(p);
    if (n && codes.includes(n)) exact.push(p);
    else if (nameHas(p.name, ws) || (n.length >= MIN_PART_CODE && codes.some((c) => c.includes(n)))) partial.push(p);
  });
  return { exact, hits: [...exact, ...partial] };
}

// Styles whose name has every word typed, or with a size whose code holds it.
export function matchCountStyles(styles, term) {
  const ws = words(term);
  const n = normCode(term);
  if (ws.length === 0) return [];
  return (styles || []).filter((s) => nameHas(s.name, ws) || (n.length >= MIN_PART_CODE && (s.variants || []).some((v) => codesOf(v.product || {}).some((c) => c.includes(n)))));
}

// The one size Enter should count: only a full code, SKU or barcode match. A
// lone name or part-code match is shown instead, so a typo never adds a count
// to the wrong size.
export function countTarget(match) {
  return match.exact.length === 1 ? match.exact[0] : null;
}
